import type { TFunction } from 'i18next';
import { formatDate } from '@/lib/utils';
import { getPermissionLabel } from '@/lib/permission-labels';

/** 'brandColor' -> 'Brand color' (last-resort label for an audit field key that has no translation). */
export function humanizeKey(key: string): string {
  const s = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}

/** POS-I305: action badge text (CREATE, UPDATE...) in the user's language; an unknown action shows humanized. */
export const historyAction = (t: TFunction, action: string): string =>
  t(`historyAction_${action}`, { defaultValue: humanizeKey(action) });

/** POS-I305: area (audit entity) name; 'TaxRuleSettings' -> translated, unknown -> humanized. */
export const historyEntity = (t: TFunction, entity: string): string =>
  t(`historyEntity_${entity}`, { defaultValue: humanizeKey(entity) });

/** POS-I305: field key of an audit diff ('brandColor'); unknown -> humanized. */
export const historyField = (t: TFunction, key: string): string =>
  t(`historyField_${key}`, { defaultValue: humanizeKey(key) });

/** POS-I309: technical record fields (ids, tenant, test flag, timestamps, secrets) are not business changes and are not listed. */
const HIDDEN_KEYS = new Set(['id', 'tenantId', 'isTest', 'createdAt', 'updatedAt', 'deletedAt', 'keyHash', 'secret', 'tenant']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

export function isHiddenHistoryKey(key: string, value?: unknown): boolean {
  if (HIDDEN_KEYS.has(key)) return true;
  return typeof value === 'string' && UUID.test(value);
}

const ROLES_MESSAGE = /^Created (\d+) role\(s\), skipped (\d+) existing\.$/;

/** POS-I305: a value of the diff. Booleans and the known backend sentence are translated; other values are shown as stored. */
export function historyValue(t: TFunction, v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return t(v ? 'historyTrue' : 'historyFalse');
  if (typeof v === 'string') {
    const m = ROLES_MESSAGE.exec(v);
    if (m) return t('historyRolesCreated', { created: Number(m[1]), skipped: Number(m[2]) });
    // POS-I310: a list stored as JSON text (Role.permissions) reads as a list, never raw brackets.
    const trimmed = v.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const arr: unknown = JSON.parse(trimmed);
        if (Array.isArray(arr)) {
          return arr.length
            ? arr.map((x) => (typeof x === 'string' ? getPermissionLabel(x, (k) => t(k) as string) : historyValue(t, x))).join(', ')
            : t('historyNone');
        }
      } catch { /* not JSON: show as stored */ }
    }
    if (ISO_DATE.test(v)) return formatDate(v);
    const e = t(`historyEnum_${v}`, { defaultValue: '' }) || t(`historyMethod_${v}`, { defaultValue: '' });
    if (e) return e;
    return /^[A-Z]+(_[A-Z]+)+$/.test(v) ? humanizeKey(v) : v;
  }
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' && x !== null ? `(${historyValue(t, x)})` : historyValue(t, x))).join(', ') : t('historyNone');
  if (typeof v === 'object') {
    if (Object.keys(v as object).length === 0) return t('historyNone');
    // POS-I308: nested records (e.g. payment-method tax rules) read as "Card: (Name: Sales Tax; Rate: 5)", never raw JSON.
    return Object.entries(v as Record<string, unknown>)
      .filter(([k, x]) => !isHiddenHistoryKey(k, x))
      .map(([k, x]) => {
        const inner = historyValue(t, x);
        return `${historyKey(t, k)}: ${typeof x === 'object' && x !== null ? `(${inner})` : inner}`;
      })
      .join('; ');
  }
  return String(v);
}

/** POS-I308: a key inside a nested value: a payment method code (CARD) or a field key. */
export const historyKey = (t: TFunction, key: string): string =>
  t(`historyMethod_${key}`, { defaultValue: '' }) || historyField(t, key);
